import { AlertCircle, LogOut, Trash2, UserPlus, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { AppContainer } from "../components/AppContainer";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import type { FamilyUser } from "./store";
import { THEMES, THEME_GROUPS, type ThemeId } from "../themes/registry";
import { applyTheme } from "../theme";

export interface SessionUser extends FamilyUser {}

interface ApiError { error?: { message?: string } }

async function apiError(response: Response) {
  const body = await response.json().catch(() => null) as ApiError | null;
  return body?.error?.message ?? "Something went wrong. Please try again.";
}

function nextPath() {
  const next = new URLSearchParams(window.location.search).get("next");
  return next?.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void fetch("/api/auth/session", { cache: "no-store" }).then(response => {
      if (response.ok) window.location.replace(nextPath());
    });
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) throw new Error(await apiError(response));
      window.location.assign(nextPath());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return <main className="auth-shell login-shell"><section className="auth-card login-card">
    <h1>Hello!</h1>
    <p className="auth-intro">Sign in to your account</p>
    <form className="auth-form" autoComplete="on" onSubmit={submit}>
      <label>Username<input name="login-username" autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={event => setUsername(event.target.value)} required /></label>
      <label>Password<input name="login-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /></label>
      {error && <p className="auth-error" role="alert"><AlertCircle size={17} />{error}</p>}
      <button className="primary-button" disabled={submitting}>{submitting ? "Signing in…" : "Sign in"}</button>
    </form>
  </section></main>;
}

export function AccountMenu({ user }: { user: SessionUser }) {
  const [busy, setBusy] = useState(false);
  async function logout() {
    setBusy(true);
    await fetch("/api/auth/logout", { method: "POST" });
    window.location.assign("/login");
  }
  return <div className="account-menu">
    <a href="/settings/" className="account-profile"><span aria-hidden="true">{user.displayName.charAt(0).toUpperCase()}</span>{user.displayName}</a>
    {user.isAdmin && <a href="/admin/" className="account-link">People</a>}
    <button type="button" className="icon-text-button" onClick={() => void logout()} disabled={busy}><LogOut size={16} />Sign out</button>
  </div>;
}

export function SettingsPage({ user, refreshUser }: { user: SessionUser; refreshUser: () => Promise<void> }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedTheme, setSelectedTheme] = useState<ThemeId>(user.theme);
  const [themeStatus, setThemeStatus] = useState("Selected");
  const selectedThemeRef = useRef<ThemeId>(user.theme);
  const confirmedThemeRef = useRef<ThemeId>(user.theme);
  const themeSaveChain = useRef(Promise.resolve());
  const passwordSectionRef = useRef<HTMLElement>(null);
  const required = new URLSearchParams(window.location.search).get("required") === "1" || user.mustChangePassword;

  useEffect(() => {
    if (!required) return;
    const frame = window.requestAnimationFrame(() => passwordSectionRef.current?.scrollIntoView({ block: "start" }));
    return () => window.cancelAnimationFrame(frame);
  }, [required]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(""); setError("");
    if (nextPassword !== confirmation) { setError("Your new passwords do not match."); return; }
    const response = await fetch("/api/auth/password", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ currentPassword, nextPassword }) });
    if (!response.ok) { setError(await apiError(response)); return; }
    setCurrentPassword(""); setNextPassword(""); setConfirmation(""); setMessage("Password changed. Your other devices have been signed out.");
    await refreshUser();
  }

  async function selectTheme(theme: ThemeId) {
    if (theme === selectedTheme) return;
    selectedThemeRef.current = theme;
    setSelectedTheme(theme); applyTheme(theme); setThemeStatus("Saving…");
    themeSaveChain.current = themeSaveChain.current.catch(() => undefined).then(async () => {
      const response = await fetch("/api/auth/theme", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ theme }) });
      if (!response.ok) {
        if (selectedThemeRef.current === theme) {
          selectedThemeRef.current = confirmedThemeRef.current;
          setSelectedTheme(confirmedThemeRef.current); applyTheme(confirmedThemeRef.current); setThemeStatus("Could not save theme.");
        }
        throw new Error("Could not save theme.");
      }
      const data = await response.json() as { user: SessionUser };
      confirmedThemeRef.current = data.user.theme;
      if (selectedThemeRef.current === theme) {
        setSelectedTheme(data.user.theme); applyTheme(data.user.theme); setThemeStatus("Saved");
      }
      await refreshUser();
    });
    await themeSaveChain.current.catch(() => undefined);
  }

  return <AppContainer className="account-shell settings-shell" contentClassName="account-content" currentDestination="home" title="Settings" asHeading backHref="/" backLabel="Back to Home">
    <section className="settings-card theme-section" aria-labelledby="theme-heading">
      <h2 id="theme-heading">Appearance</h2>
      <fieldset className="theme-options" aria-describedby="theme-status">
        <legend className="sr-only">Theme</legend>
        {THEME_GROUPS.map(group => <div className="theme-option-group" role="group" aria-label={group.label} key={group.label}>
          {group.ids.map(id => {
            const theme = THEMES.find(candidate => candidate.id === id)!;
            return <label className="theme-option" key={theme.id}>
              <input type="radio" name="theme" autoComplete="off" value={theme.id} checked={selectedTheme === theme.id} onChange={() => void selectTheme(theme.id)} />
              <span className="theme-preview" data-theme={theme.id} aria-hidden="true"><i /><b /><em /></span><span>{theme.label}</span>
            </label>;
          })}
        </div>)}
      </fieldset>
      <p className="sr-only" id="theme-status" role="status">{themeStatus}</p>
    </section>
    <section ref={passwordSectionRef} className="settings-card password-section" aria-labelledby="password-heading">
      <h2 id="password-heading">Password</h2>
      {required && <p className="required-note" role="alert">Choose a permanent password to continue.</p>}
      <form className="auth-form" autoComplete="off" onSubmit={event => void submit(event)}>
      <label>Current password<input name="account-current-secret" type="password" autoComplete="off" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} required /></label>
      <label>New password<input name="account-new-secret" type="password" minLength={12} autoComplete="off" value={nextPassword} onChange={event => setNextPassword(event.target.value)} required /></label>
      <label>Confirm new password<input name="account-new-secret-confirmation" type="password" minLength={12} autoComplete="off" value={confirmation} onChange={event => setConfirmation(event.target.value)} required /></label>
      <p className="field-hint">12 characters minimum</p>
      {error && <p className="auth-error" role="alert"><AlertCircle size={17} />{error}</p>}
      {message && <p className="success-note" role="status">{message}</p>}
      <button className="primary-button">Change password</button>
      </form>
    </section>
  </AppContainer>;
}

export function AdminUserRow({ user, currentUserId, deleting, onRename, onReset, onToggleAdmin, onDelete }: {
  user: SessionUser;
  currentUserId: number;
  deleting: boolean;
  onRename: (user: SessionUser) => void;
  onReset: (user: SessionUser) => void;
  onToggleAdmin: (user: SessionUser) => void;
  onDelete: (user: SessionUser, trigger: HTMLButtonElement) => void;
}) {
  const isCurrentUser = user.id === currentUserId;
  return <article className="user-row" key={user.id}>
    <div className="user-identity"><strong>{user.displayName}</strong><span>@{user.username}{user.isAdmin ? " · Administrator" : ""}{user.isDisabled ? " · Disabled" : ""}</span>{isCurrentUser && <small id={`user-delete-hint-${user.id}`}>This is your account.</small>}</div>
    <div className="user-actions">
      <button type="button" className="utility-button" onClick={() => onRename(user)}>Rename</button>
      <button type="button" className="utility-button" onClick={() => onReset(user)}>Reset password</button>
      <button type="button" className="utility-button" onClick={() => onToggleAdmin(user)} disabled={isCurrentUser}>{user.isAdmin ? "Remove admin" : "Make admin"}</button>
      <button type="button" className="utility-button user-delete-button" onClick={event => onDelete(user, event.currentTarget)} disabled={isCurrentUser || deleting} aria-describedby={isCurrentUser ? `user-delete-hint-${user.id}` : undefined} title={isCurrentUser ? "You cannot delete your own account." : undefined}><Trash2 size={16} aria-hidden="true" />Delete</button>
    </div>
  </article>;
}

export function AdminPage({ currentUser }: { currentUser: SessionUser }) {
  const [users, setUsers] = useState<SessionUser[]>([]);
  const [error, setError] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SessionUser | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const deleteTrigger = useRef<HTMLButtonElement | null>(null);

  async function load() {
    const response = await fetch("/api/admin/users", { cache: "no-store" });
    if (!response.ok) { setError(await apiError(response)); return; }
    setError("");
    setUsers((await response.json() as { users: SessionUser[] }).users);
  }
  useEffect(() => { void load(); }, []);

  async function addUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const response = await fetch("/api/admin/users", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, displayName, password, isAdmin }) });
    if (!response.ok) { setError(await apiError(response)); return; }
    setUsername(""); setDisplayName(""); setPassword(""); setIsAdmin(false); setAddingUser(false); await load();
  }

  async function saveUser(user: SessionUser, changes: Partial<Pick<SessionUser, "displayName" | "isAdmin" | "isDisabled">>) {
    setError("");
    const response = await fetch(`/api/admin/users/${user.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(changes) });
    if (!response.ok) { setError(await apiError(response)); return; }
    await load();
  }

  async function reset(user: SessionUser) {
    const temporaryPassword = window.prompt(`Enter a temporary password for ${user.displayName} (at least 12 characters):`);
    if (!temporaryPassword) return;
    const response = await fetch(`/api/admin/users/${user.id}/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: temporaryPassword }) });
    if (!response.ok) { setError(await apiError(response)); return; }
    await load();
  }

  function rename(user: SessionUser) {
    const displayName = window.prompt(`Change ${user.username}'s display name:`, user.displayName);
    if (!displayName || displayName === user.displayName) return;
    void saveUser(user, { displayName });
  }

  function openDelete(user: SessionUser, trigger: HTMLButtonElement) {
    deleteTrigger.current = trigger;
    setPendingDelete(user);
  }

  async function removeUser(user: SessionUser) {
    if (deletingId !== null) return;
    setError("");
    setDeletingId(user.id);
    let response: Response;
    try {
      response = await fetch(`/api/admin/users/${user.id}`, { method: "DELETE" });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete user.");
      setDeletingId(null);
      return;
    }
    if (!response.ok) {
      setError(await apiError(response));
      setDeletingId(null);
      return;
    }
    setPendingDelete(null);
    setDeletingId(null);
    await load();
    const trigger = deleteTrigger.current;
    if (trigger?.isConnected) trigger.focus();
    else addButton.current?.focus();
  }

  return <AppContainer className="account-shell admin-shell" contentClassName="account-content" currentDestination="home" title="People" asHeading backHref="/" backLabel="Back to Home" actions={<button ref={addButton} className="primary-button compact-button admin-add-button" type="button" onClick={() => setAddingUser(true)} disabled={addingUser} aria-expanded={addingUser}><UserPlus size={18} /><span>Add member</span></button>}>
    {error && <p className="auth-error" role="alert"><AlertCircle size={17} />{error}</p>}
    <section className="people-section" aria-labelledby="people-heading">
      <div className="settings-card-heading">
        <h2 id="people-heading"><Users size={20} aria-hidden="true" />Family members</h2>
      </div>
      <div className="user-list" aria-label="Family members">{users.map(user => <AdminUserRow key={user.id} user={user} currentUserId={currentUser.id} deleting={deletingId !== null} onRename={rename} onReset={userToReset => void reset(userToReset)} onToggleAdmin={userToToggle => void saveUser(userToToggle, { isAdmin: !userToToggle.isAdmin })} onDelete={openDelete} />)}</div>
    </section>
    {addingUser && <section className="settings-card add-member-section" aria-labelledby="add-member-heading">
      <h2 id="add-member-heading"><UserPlus size={20} aria-hidden="true" />Add family member</h2>
      <form className="new-user-form" autoComplete="off" onSubmit={event => void addUser(event)}>
      <label>Name<input autoFocus name="admin-display-name" autoComplete="off" autoCapitalize="words" value={displayName} onChange={event => setDisplayName(event.target.value)} required /></label>
      <label>Username<input name="admin-account-id" autoComplete="off" value={username} onChange={event => setUsername(event.target.value)} autoCapitalize="none" spellCheck={false} required /></label>
      <label>Temporary password<input name="admin-temporary-secret" type="password" autoComplete="off" value={password} onChange={event => setPassword(event.target.value)} minLength={12} required /></label>
      <label className="check-label"><input name="admin-permission" type="checkbox" autoComplete="off" checked={isAdmin} onChange={event => setIsAdmin(event.target.checked)} /> Administrator</label>
      <div className="form-actions"><button className="secondary-button" type="button" onClick={() => setAddingUser(false)}>Cancel</button><button className="primary-button">Add member</button></div>
      </form>
    </section>}
    {pendingDelete && <DeleteUserDialog user={pendingDelete} busy={deletingId === pendingDelete.id} onCancel={() => { if (deletingId === null) setPendingDelete(null); }} onConfirm={() => void removeUser(pendingDelete)} />}
  </AppContainer>;
}

export function DeleteUserDialog({ user, busy, onCancel, onConfirm }: {
  user: SessionUser;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return <DeleteConfirmDialog title={<>Delete “{user.displayName}”?</>} titleId="delete-user-title" description={<>This permanently removes {user.displayName} (@{user.username}) and all of their sessions. This cannot be undone.</>} descriptionId="delete-user-description" busy={busy} onCancel={onCancel} onConfirm={onConfirm} confirmLabel="Delete account" backdropLabel="Cancel account deletion" />;
}
